// src/components/patient/assistant/AssistantFilters.jsx
//
// AVIA's structured search controls. They edit the SAME server-side search
// state as typed refinements, using the canonical discovery filter names the
// booking screen uses (specialty, service_type_id, clinic_id, doctor_id,
// location, language, mode, doctor_gender, price_min/max, date_from/to) plus
// the explicit flexible answers date_any / location_any.
//
//   * FilterBar: compact removable chips for the active filters + Edit filters.
//   * FilterEditor: the form. Options come from the live discovery catalogue
//     (never typed ids); price bounds show the currency and min <= max and
//     from <= to are checked before anything is sent. The server validates
//     again and applies every eligibility rule itself.

import React, { useMemo, useState } from "react";

// Chip -> the `clear` key the API accepts.
const CHIPS = [
  ["specialty", "Speciality", "specialty"],
  ["service_name", "Service", "service_name"],
  ["service_type_label", "Service type", "service_type_id"],
  ["doctor_label", "Doctor", "doctor_id"],
  ["doctor_name", "Doctor", "doctor_name"],
  ["clinic_label", "Clinic", "clinic_id"],
  ["clinic_name", "Clinic", "clinic_name"],
  ["location", "Location", "location"],
  ["mode", "Consultation", "mode"],
  ["doctor_gender", "Provider", "doctor_gender"],
  ["language", "Language", "language"],
  ["time_label", "Time", "time"],
];

const MODE_TEXT = { video: "Online (video)", in_clinic: "In-clinic" };
const GENDER_TEXT = { female: "Female doctors", male: "Male doctors" };

function title(value) {
  return String(value || "").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function activeChips(filters, currency) {
  if (!filters) return [];
  const chips = [];
  CHIPS.forEach(([key, label, clear]) => {
    const value = filters[key];
    if (!value) return;
    let text = value;
    if (key === "mode") text = MODE_TEXT[value] || value;
    if (key === "doctor_gender") text = GENDER_TEXT[value] || value;
    if (key === "specialty" || key === "location") text = title(value);
    chips.push({ key, label, text, clear });
  });
  if (filters.location_any) chips.push({ key: "location_any", label: "Location", text: "Anywhere", clear: "location" });
  if (filters.price_min || filters.price_max) {
    const unit = currency ? ` ${currency}` : "";
    const text = filters.price_min && filters.price_max ? `${filters.price_min}–${filters.price_max}${unit}`
      : filters.price_max ? `Up to ${filters.price_max}${unit}` : `From ${filters.price_min}${unit}`;
    chips.push({ key: "price", label: "Price", text, clear: "price" });
  }
  if (filters.date_any) chips.push({ key: "date_any", label: "Date", text: "Any date", clear: "dates" });
  else if (filters.date_from) {
    const text = filters.date_to && filters.date_to !== filters.date_from ? `${filters.date_from} → ${filters.date_to}` : filters.date_from;
    chips.push({ key: "dates", label: "Date", text, clear: "dates" });
  }
  return chips;
}

export function FilterBar({ filters, currency, disabled, onRemove, onEdit }) {
  const chips = activeChips(filters, currency);
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 bg-white px-4 py-2" aria-label="Active search filters" data-testid="assistant-filter-bar">
      {chips.map((chip) => (
        <span key={chip.key} className="inline-flex max-w-full items-center gap-1 rounded-full border border-violet-200 bg-violet-50 py-0.5 pl-2.5 pr-1 text-xs text-violet-800">
          <span className="font-semibold">{chip.label}:</span>
          <span className="truncate">{chip.text}</span>
          <button type="button" disabled={disabled} onClick={() => onRemove(chip.clear)} aria-label={`Remove ${chip.label.toLowerCase()} filter`}
            className="ml-0.5 rounded-full px-1 leading-none text-violet-600 hover:bg-violet-100 disabled:opacity-50">
            ×
          </button>
        </span>
      ))}
      <button type="button" onClick={onEdit} disabled={disabled} className="ml-auto rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
        Edit filters
      </button>
    </div>
  );
}

const FIELD = "mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-200";

function Select({ id, label, value, onChange, options, placeholder }) {
  return (
    <div>
      <label htmlFor={id} className="text-xs font-semibold text-slate-700">{label}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={FIELD}>
        <option value="">{placeholder}</option>
        {(options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export function FilterEditor({ filters, options, optionsError, onSubmit, onCancel, onUseLocation, locating }) {
  const f = filters || {};
  const [form, setForm] = useState(() => ({
    specialty: f.specialty || "",
    service_type_id: f.service_type_id || "",
    clinic_id: f.clinic_id || "",
    doctor_id: f.doctor_id || "",
    location: f.location_any ? "__any" : f.location || "",
    language: f.language || "",
    mode: f.mode || "",
    doctor_gender: f.doctor_gender || "",
    price_min: f.price_min || "",
    price_max: f.price_max || "",
    date_from: f.date_from || "",
    date_to: f.date_to || "",
    date_any: Boolean(f.date_any),
  }));
  const [problem, setProblem] = useState("");
  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  const currencies = options?.currencies || [];
  const currencyText = currencies.length === 1 ? currencies[0] : currencies.length > 1 ? "each service's currency" : "";
  // Location choices are real bookable cities, matched case-insensitively to what was typed.
  const locationOptions = useMemo(() => {
    const list = (options?.locations || []).map((o) => ({ value: o.value, label: o.label }));
    if (form.location && form.location !== "__any" && !list.some((o) => o.value.toLowerCase() === form.location.toLowerCase())) {
      list.unshift({ value: form.location, label: title(form.location) });
    }
    return list;
  }, [options, form.location]);
  const specialtyOptions = useMemo(() => {
    const list = (options?.specialties || []).map((o) => ({ value: String(o.value).toLowerCase(), label: o.label }));
    if (form.specialty && !list.some((o) => o.value === form.specialty.toLowerCase())) list.unshift({ value: form.specialty, label: title(form.specialty) });
    return list;
  }, [options, form.specialty]);

  function submit(event) {
    event.preventDefault();
    const min = form.price_min === "" ? null : Number(form.price_min);
    const max = form.price_max === "" ? null : Number(form.price_max);
    if ((min !== null && (!Number.isFinite(min) || min < 0)) || (max !== null && (!Number.isFinite(max) || max < 0))) {
      setProblem("Prices must be zero or more.");
      return;
    }
    if (min !== null && max !== null && min > max) {
      setProblem("Minimum price cannot be more than the maximum price.");
      return;
    }
    if (!form.date_any && form.date_from && form.date_to && form.date_to < form.date_from) {
      setProblem("The end date must be on or after the start date.");
      return;
    }
    setProblem("");
    const filtersOut = {};
    const clear = [];
    const keep = (key, value, clearKey = key) => (value === "" || value === null ? clear.push(clearKey) : (filtersOut[key] = value));
    keep("specialty", form.specialty);
    keep("service_type_id", form.service_type_id);
    keep("clinic_id", form.clinic_id);
    keep("doctor_id", form.doctor_id);
    keep("language", form.language);
    keep("mode", form.mode);
    keep("doctor_gender", form.doctor_gender);
    if (form.location === "__any") filtersOut.location_any = true;
    else keep("location", form.location);
    if (min === null && max === null) clear.push("price");
    else {
      if (min !== null) filtersOut.price_min = String(min);
      if (max !== null) filtersOut.price_max = String(max);
    }
    if (form.date_any) filtersOut.date_any = true;
    else if (form.date_from) {
      filtersOut.date_from = form.date_from;
      filtersOut.date_to = form.date_to || form.date_from;
    } else clear.push("dates");
    onSubmit({ filters: filtersOut, clear: Array.from(new Set(clear)) });
  }

  return (
    <form onSubmit={submit} aria-label="Edit search filters" data-testid="assistant-filter-editor">
      {optionsError ? <p role="alert" className="mb-2 text-xs text-amber-800">{optionsError}</p> : null}
      <div className="grid max-h-[60vh] grid-cols-1 gap-3 overflow-y-auto pr-1 sm:grid-cols-2">
        <Select id="af-specialty" label="Speciality" value={form.specialty} onChange={set("specialty")} options={specialtyOptions} placeholder="Any speciality" />
        <Select id="af-type" label="Service type" value={form.service_type_id} onChange={set("service_type_id")} options={options?.service_types} placeholder="Any service type" />
        <div className="sm:col-span-2">
          <label htmlFor="af-location" className="text-xs font-semibold text-slate-700">Location</label>
          <div className="flex gap-2">
            <select id="af-location" value={form.location} onChange={(e) => set("location")(e.target.value)} className={FIELD}>
              <option value="">Not set</option>
              <option value="__any">Anywhere</option>
              {locationOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {onUseLocation ? (
              <button type="button" onClick={onUseLocation} disabled={locating} className="mt-1 shrink-0 rounded-xl border border-slate-300 px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
                {locating ? "Locating…" : "Use my location"}
              </button>
            ) : null}
          </div>
        </div>
        <Select id="af-mode" label="Consultation" value={form.mode} onChange={set("mode")} options={(options?.modes || []).map((m) => ({ value: m.value, label: MODE_TEXT[m.value] || m.label }))} placeholder="Online or in-clinic" />
        <Select id="af-gender" label="Provider gender" value={form.doctor_gender} onChange={set("doctor_gender")} options={options?.genders} placeholder="Any provider" />
        <div>
          <label htmlFor="af-price-min" className="text-xs font-semibold text-slate-700">Min price{currencyText ? ` (${currencyText})` : ""}</label>
          <input id="af-price-min" type="number" min="0" inputMode="decimal" value={form.price_min} onChange={(e) => set("price_min")(e.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor="af-price-max" className="text-xs font-semibold text-slate-700">Max price{currencyText ? ` (${currencyText})` : ""}</label>
          <input id="af-price-max" type="number" min="0" inputMode="decimal" value={form.price_max} onChange={(e) => set("price_max")(e.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor="af-from" className="text-xs font-semibold text-slate-700">From</label>
          <input id="af-from" type="date" disabled={form.date_any} value={form.date_from} onChange={(e) => set("date_from")(e.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor="af-to" className="text-xs font-semibold text-slate-700">To</label>
          <input id="af-to" type="date" disabled={form.date_any} value={form.date_to} min={form.date_from || undefined} onChange={(e) => set("date_to")(e.target.value)} className={FIELD} />
        </div>
        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input type="checkbox" checked={form.date_any} onChange={(e) => set("date_any")(e.target.checked)} />
          Any date is fine
        </label>
        <Select id="af-language" label="Language" value={form.language} onChange={set("language")} options={options?.languages} placeholder="Any language" />
        <Select id="af-clinic" label="Clinic" value={form.clinic_id} onChange={set("clinic_id")} options={options?.clinics} placeholder="Any clinic" />
        <Select id="af-doctor" label="Doctor" value={form.doctor_id} onChange={set("doctor_id")} options={options?.doctors} placeholder="Any doctor" />
      </div>
      {problem ? <p role="alert" className="mt-3 text-sm font-semibold text-rose-700">{problem}</p> : null}
      <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button type="button" onClick={onCancel} className="rounded-2xl border border-slate-300 px-4 py-2 text-sm font-semibold">Cancel</button>
        <button type="submit" className="rounded-2xl bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-500 px-4 py-2 text-sm font-semibold text-white">Apply filters</button>
      </div>
    </form>
  );
}
